import { ALL_DESTINATIONS, type WorkspaceNavPath } from './navigation';

const WORKSPACE_PATH = /^\/workspaces\/[^/]+(?<rest>\/.+)?$/;

export interface WorkspaceSwitchTarget {
  to: WorkspaceNavPath;
  params: { workspaceId: string };
}

/**
 * Where switching to another workspace lands.
 *
 * Static sections carry over — someone comparing workflows across workspaces
 * stays on Workflows. Entity-scoped routes (a workflow's board or builder)
 * fall back to the Workflows list, because the entity belongs to the
 * workspace being left. Anything unrecognised lands on the overview.
 *
 * Search params are dropped by the caller for the same reason: the open task
 * and board state reference entities of the previous workspace.
 */
export function workspaceSwitchTarget(
  pathname: string,
  workspaceId: string,
): WorkspaceSwitchTarget {
  const rest = WORKSPACE_PATH.exec(pathname)?.groups?.rest ?? '';
  // A board's path folds to Workflows, not to the overview.
  //
  // Both `/workflows/{key}/...` and the kept `/boards...` addresses name a
  // workflow in the workspace being left, and neither names anything in the one
  // being entered — so the section is what carries over, never the identifier.
  // `/boards` stopped matching a nav item when the two sections merged (spec
  // 094), and without this it silently fell through to the overview: switching
  // workspaces from a board took you somewhere unrelated rather than to the same
  // section in the new workspace.
  const section =
    rest.startsWith('/workflows/') || rest.startsWith('/boards')
      ? '/workflows'
      : rest;
  const match = ALL_DESTINATIONS.find(
    (item) => item.to === `/workspaces/$workspaceId${section}`,
  );
  return {
    to: match?.to ?? '/workspaces/$workspaceId',
    params: { workspaceId },
  };
}
